/**
 * 1.4.1: 基本信息标签页组件
 * 账号中心的基本信息 Tab，包含显示名称、邮箱、密码修改
 * 遵循 YUI 主题设计规范
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Lock, Eye, EyeOff, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getCurrentUser, updateUserProfile, updateUserPassword, getUserProviders } from '../../services/authService';
import { isSupabaseAvailable } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { validatePasswordStrength } from '../../utils/passwordValidation';

export function BasicInfoTab() {
  const { t } = useTranslation();
  const [user, setUser] = useState<any>(null);
  const [providers, setProviders] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // 表单状态
  const [displayName, setDisplayName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI 状态
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  
  useEffect(() => {
    loadUserInfo();
  }, []);
  
  const loadUserInfo = async () => {
    if (!isSupabaseAvailable) {
      setIsLoading(false);
      return;
    }
    
    try {
      const currentUser = await getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        // 设置显示名称
        const metadata = currentUser.user_metadata || {};
        const name = metadata.full_name || metadata.name || currentUser.email?.split('@')[0] || '';
        setDisplayName(name);
        
        // 获取登录方式
        const userProviders = getUserProviders(currentUser);
        setProviders(userProviders);
      }
    } catch (error) {
      logger.error('Error loading user info:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleUpdateProfile = async () => {
    if (!displayName.trim()) {
      toast.error(t('pleaseEnterName', '请输入显示名称'));
      return;
    }
    
    setIsSavingProfile(true);
    
    try {
      const { error } = await updateUserProfile({ full_name: displayName });
      
      if (error) {
        logger.error('Error updating profile:', error);
        toast.error(t('updateProfileFailed', '更新失败'));
      } else {
        toast.success(t('updateProfileSuccess', '更新成功'));
        await loadUserInfo();
      }
    } catch (error) {
      logger.error('Error updating profile:', error);
      toast.error(t('updateProfileFailed', '更新失败'));
    } finally {
      setIsSavingProfile(false);
    }
  };
  
  const handleUpdatePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error(t('pleaseEnterNewPassword', '请输入新密码'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(t('passwordMismatch', '两次输入的密码不一致'));
      return;
    }
    
    const pwValidation = validatePasswordStrength(newPassword);
    if (!pwValidation.isValid) {
      toast.error(t('passwordRequirement'));
      return;
    }
    
    setIsSavingPassword(true);
    
    try {
      const { error } = await updateUserPassword(newPassword);
      
      if (error) {
        logger.error('Error updating password:', error);
        toast.error(t('updatePasswordFailed', '密码修改失败'));
      } else {
        toast.success(t('updatePasswordSuccess', '密码修改成功'));
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      logger.error('Error updating password:', error);
      toast.error(t('updatePasswordFailed', '密码修改失败'));
    } finally {
      setIsSavingPassword(false);
    }
  };
  
  const isThirdPartyLogin = providers.length > 0 && !providers.includes('email');
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="inline-block text-primary font-semibold yui-loading-animation text-2xl">YUI</span>
      </div>
    );
  }
  
  if (!user) {
    return (
      <div className="text-center py-16 text-gray-500">
        {t('pleaseLogin', '请先登录')}
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* 基本信息 */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-5">{t('basicInfo', '基本信息')}</h3>
        
        {/* 用户名 */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-2" />
            {t('displayName', '显示名称')}
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder={t('pleaseEnterName', '请输入显示名称')}
            />
            <button
              onClick={handleUpdateProfile}
              disabled={isSavingProfile}
              className="px-5 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {isSavingProfile ? t('saving', '保存中...') : t('save', '保存')}
            </button>
          </div>
        </div>
        
        {/* 邮箱 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            {t('email', '邮箱')}
          </label>
          <input
            type="email"
            value={user.email || ''}
            disabled
            className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
          />
          <p className="text-sm text-gray-400 mt-2">
            💡 {t('emailCannotBeChanged', '邮箱地址不可修改')}
          </p>
        </div>
        
        {/* 登录方式（仅第三方登录显示） */}
        {isThirdPartyLogin && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('loginMethod', '登录方式')}
            </label>
            <div className="flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-xl bg-gray-50">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-gray-900 font-medium capitalize">
                {providers.map(p => p === 'google' ? 'Google' : p).join(', ')}
              </span>
            </div>
            <p className="text-sm text-gray-400 mt-2">
              {t('thirdPartyLoginNote', '使用第三方账号登录，无法修改密码')}
            </p>
          </div>
        )}
      </div>
      
      {/* 密码修改（仅邮箱登录显示） */}
      {!isThirdPartyLogin && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-5 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            {t('changePassword', '修改密码')}
          </h3>
          
          {/* 新密码 */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('newPassword', '新密码')}
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder={t('pleaseEnterNewPassword', '请输入新密码')}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          {/* 确认密码 */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('confirmPassword', '确认密码')}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder={t('pleaseConfirmPassword', '请再次输入密码')}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          <button
            onClick={handleUpdatePassword}
            disabled={isSavingPassword}
            className="w-full px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Lock className="w-4 h-4" />
            {isSavingPassword ? t('updating', '更新中...') : t('updatePassword', '修改密码')}
          </button>
          
          <p className="text-sm text-gray-400 mt-3 text-center">
            {t('passwordRequirement', '密码至少8位，须包含字母、数字和特殊符号')}
          </p>
        </div>
      )}
    </div>
  );
}
